import { useRef } from "react";
import { Controller, useFormContext } from "react-hook-form";

import { MultiSelect2 } from "@/components/multi-select-2/MultiSelect2";
import { useGetProjectDetailsOptions } from "@/features/projects/project/details/hooks/useGetProjectDetailsOptions";

import { useProjectDetails } from "../useProjectDetails";

export function TeamDetailsSection() {
  const { control, getValues, setValue } = useFormContext();
  const { isDesignerPrefsReadOnly, isReadOnly, projectId } =
    useProjectDetails();

  const { data: options } = useGetProjectDetailsOptions(projectId);

  const teams = [...(options?.teams || [])].sort((a, b) =>
    a.name.localeCompare(b.name),
  );
  const designers = options?.designers || [];

  const designerOptions = designers.map((d) => ({
    avatar: d.avatar,
    description: d.designation ?? undefined,
    href: `/users/${d.id}/edit`,
    id: Number(d.id),
    textValue: d.name,
  }));

  // Tracks designers auto-removed from each list due to a conflict, so they
  // can be restored if the user reverts the change that caused the conflict.
  const autoRemovedRef = useRef<{
    nonPreferredDesignerIds: Set<number>;
    preferredDesignerIds: Set<number>;
  }>({ nonPreferredDesignerIds: new Set(), preferredDesignerIds: new Set() });

  const makeDesignerChangeHandler =
    (
      field: { onChange: (value: number[]) => void },
      otherField: "nonPreferredDesignerIds" | "preferredDesignerIds",
    ) =>
    (newIds: number[]) => {
      field.onChange(newIds);
      const otherIds: number[] = getValues(otherField) ?? [];
      const autoRemoved = autoRemovedRef.current[otherField];

      const newConflicts = newIds.filter((id) => otherIds.includes(id));
      // Items no longer conflicting (not in current selection); split by whether
      // they need restoring or were already re-added by the user manually.
      const noLongerConflicting = [...autoRemoved].filter(
        (id) => !newIds.includes(id),
      );
      const toRestore = noLongerConflicting.filter(
        (id) => !otherIds.includes(id),
      );

      for (const id of newConflicts) autoRemoved.add(id);
      for (const id of noLongerConflicting) autoRemoved.delete(id);

      if (newConflicts.length > 0 || toRestore.length > 0) {
        setValue(
          otherField,
          [
            ...otherIds.filter((id) => !newConflicts.includes(id)),
            ...toRestore,
          ],
          { shouldDirty: true },
        );
      }
    };

  return (
    <div className="bg-bg-primary rounded-lg px-6">
      <h3 className="text-lg text-text-primary mb-3">Team Details</h3>
      <div className="grid grid-cols-2 gap-x-5 space-y-5">
        <div className="col-span-2">
          <Controller
            control={control}
            name="assignedTeamIds"
            render={({ field }) => (
              <MultiSelect2
                isDisabled={isReadOnly}
                label="Assigned Teams"
                onChange={field.onChange}
                options={teams.map((t) => ({
                  href: `/teams/${t.id}/edit`,
                  id: t.id,
                  textValue: t.name,
                }))}
                placeholder="Select teams..."
                selectedIds={field.value || []}
              />
            )}
          />
        </div>

        <Controller
          control={control}
          name="preferredDesignerIds"
          render={({ field }) => (
            <MultiSelect2
              isDisabled={isDesignerPrefsReadOnly}
              label="Preferred Designers"
              onChange={makeDesignerChangeHandler(
                field,
                "nonPreferredDesignerIds",
              )}
              options={designerOptions}
              placeholder="Search to add preferred designers..."
              selectedIds={field.value || []}
            />
          )}
        />

        <Controller
          control={control}
          name="nonPreferredDesignerIds"
          render={({ field }) => (
            <MultiSelect2
              isDisabled={isDesignerPrefsReadOnly}
              label="Non-Preferred Designers"
              onChange={makeDesignerChangeHandler(
                field,
                "preferredDesignerIds",
              )}
              options={designerOptions}
              placeholder="Search to add non-preferred designers..."
              selectedIds={field.value || []}
            />
          )}
        />
      </div>
    </div>
  );
}
